import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { toneBadgeClass, type KpiTone } from './KpiTile';

/**
 * One tinted shortcut tile — the dashboard's Quick Actions (docs/design/
 * dashboard, region 10): a soft tone fill, icon and label in the tone's
 * strong colour. Tones are categorical identity only (see theme.css), so two
 * tiles may share one.
 *
 * `layout="stacked"` (default) is the proposal's 64px tile, icon over label.
 * `layout="inline"` puts the icon beside the label in a 40px row — the
 * Dashboard pins Quick Actions to the bottom of the screen in that form, where
 * every pixel of height is taken from the cards above it.
 */
export interface QuickActionTileProps {
  icon: ReactNode;
  label: string;
  tone: KpiTone;
  onClick: () => void;
  layout?: 'stacked' | 'inline';
  className?: string;
}

export function QuickActionTile({ icon, label, tone, onClick, layout = 'stacked', className }: QuickActionTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center justify-center rounded-md border border-black/5 px-2 text-center transition-[filter] hover:brightness-95',
        'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25',
        layout === 'inline' ? 'h-10 gap-2' : 'h-16 flex-col gap-1',
        toneBadgeClass(tone),
        className
      )}
    >
      {icon}
      <span className={cn('text-[11px] font-medium leading-tight', layout === 'inline' && 'truncate')}>{label}</span>
    </button>
  );
}
